import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { tokenManager } from '../utility/tokenManager';
import { APP_ROUTES } from '../constants';
import { TOKEN_TYPES } from '../constant/auth.constant';
import { getCurrentLang } from './langHeader';

const baseUrl = import.meta.env.VITE_BASE_URL;

/**
 * Public API Instance
 *
 * Used for endpoints that don't require authentication OR require temporary tokens:
 * - Login: POST /auth/login
 * - Request OTP: POST /auth/request-otp-email
 * - Verify OTP: POST /auth/verify-otp
 * - Reset Password: POST /auth/reset-password
 * - Submit Agreement: POST /auth/agreement/sign
 * - Change Password (First Login): POST /auth/change-password
 *
 * Supports: publicToken (temporary token for OTP flows, password reset, agreement)
 * Note: changePasswordToken from backend is stored as publicToken on frontend
 */
const publicInstance: AxiosInstance = axios.create({
  baseURL: baseUrl,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Request Interceptor: Attach public token if available
 *
 * Checks for publicToken (which includes changePasswordToken from backend)
 * and adds it to Authorization header if present.
 */
publicInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const publicToken = tokenManager.getToken(TOKEN_TYPES.PUBLIC_TOKEN);
    // Never overwrite an Authorization header set explicitly by the caller
    // (e.g. /auth/selectOrganization sends the short-lived temp token).
    if (publicToken && config.headers && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${publicToken}`;
    }
    if (config.headers) {
      config.headers['Accept-Language'] = getCurrentLang();
    }
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Response Interceptor: Handle errors and token expiration
 *
 * Handles:
 * - 401 errors on login API: Clear publicToken (don't redirect - let login handle error)
 * - 401 errors on other APIs: Clear all tokens + redirect to login (clear history)
 * - API error codes: Reject with error message
 */
publicInstance.interceptors.response.use(
  (response) => {
    if (response.data.code < 0) {
      return Promise.reject(response.data.result);
    }
    return response;
  },
  async (error: AxiosError) => {
    // Handle 401 - Unauthorized/Token Expired
    if (error.response?.status === 401) {
      // Check if this is a login API request (by checking the request URL)
      const requestUrl = error.config?.url || '';
      const isLoginApi = requestUrl.includes('/login');

      // If this is NOT a login API AND got 401, redirect to login with cleared history
      if (!isLoginApi) {
        // 1. Clear ALL tokens
        tokenManager.clearAllTokens();

        // 2. Replace current history entry with login route
        // This makes login screen the only history entry (user cannot go back)
        window.history.replaceState(null, '', APP_ROUTES.LOGIN);

        // 3. Navigate to login with replace (not push)
        // This ensures login page becomes the history root
        window.location.replace(APP_ROUTES.LOGIN);
      }
      // If IS login API and got 401: just reject (don't redirect)
      // Let the login component handle the 401 error
    }

    return Promise.reject(error.response?.data || error.message);
  },
);

export default publicInstance;
